import React, { useState, useCallback, useEffect } from 'react';
import { pickBy } from 'lodash';
import PropTypes from 'prop-types';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';

import Pagination from 'partial/components/Pagination';
import AsyncButton from 'modules/common/components/AsyncButton';
import Table from 'partial/components/Table';
import SearchBar from 'partial/components/input/SearchBar';
import { useCreateModal } from 'partial/components/Modal';
import { PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import AddUpdate from './modals/AddUpdate';
import Delete from './modals/Delete';
import { useDatasets } from './hooks';

function DataManagement({ code, editValueOnly }) {
  const { setSubLabel } = useModuleContext();

  const createModal = useCreateModal();
  const [filter, setFilter] = useState({ q: '' });
  const [isLoading, list, meta] = useDatasets(code, pickBy(filter));
  const addModal = useCallback(() => {
    createModal({
      content: (onClose) => <AddUpdate code={code} onClose={onClose} />,
    });
  }, [createModal, code]);

  const editModal = useCallback(
    (data) => {
      createModal({
        content: (onClose) => (
          <AddUpdate
            code={code}
            data={data}
            onClose={onClose}
            type="edit"
            editValueOnly={editValueOnly}
          />
        ),
      });
    },
    [createModal, code, editValueOnly],
  );

  const deleteModal = useCallback(
    (data) => {
      createModal({
        content: (onClose) => (
          <Delete code={code} data={data} onClose={onClose} />
        ),
      });
    },
    [createModal, code],
  );

  const renderAction = (data) => (
    <div className="flex gap-1">
      {!editValueOnly && (
        <AsyncButton
          color="outline"
          className="!text-xs !px-3 !py-1"
          onClick={() => deleteModal(data)}
        >
          <div className="flex gap-2 items-center !capitalize">
            <Trash /> Delete
          </div>
        </AsyncButton>
      )}

      <AsyncButton
        color="outline"
        className="!text-xs !px-3 !py-1"
        onClick={() => editModal(data)}
      >
        <div className="flex gap-2 items-center !capitalize">
          <PencilSimple /> Edit
        </div>
      </AsyncButton>
    </div>
  );

  const format = [
    {
      label: 'label',
      key: 'label',
    },
    {
      label: 'value',
      labelClassName: 'pl-8',
      contentClassName: 'w-[12rem]',
      key: (data) => <div className="w-full">{data?.value}</div>,
    },
    {
      label: 'Action',
      id: 'action',
      labelClassName: 'justify-end !pr-14',
      contentClassName: 'flex justify-end',
      key: (data) => renderAction(data),
    },
  ];

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} dataset${meta?.total === 1 ? '' : 's'}`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  return (
    <section className="px-6 h-full flex flex-col w-full gap-5">
      <div className="flex items-center justify-between space-x-5">
        <SearchBar
          name="q"
          value={filter.q}
          onChange={setFilter}
          inputClassName="py-[1.05rem] "
          containerClassName="opacity-0 pointer-events-none"
        />

        {!editValueOnly && (
          <AsyncButton onClick={() => addModal()}>
            <div className="flex gap-2 items-center">
              <Plus /> add
            </div>
          </AsyncButton>
        )}
      </div>
      <div className="flex flex-col gap-10 min-h-[65vh]">
        <Table isLoading={isLoading} data={list} format={format} />
        <Pagination value={meta} onChange={setFilter} />
      </div>
    </section>
  );
}

DataManagement.propTypes = {
  code: PropTypes.string.isRequired,
  editValueOnly: PropTypes.bool,
};

DataManagement.defaultProps = {
  editValueOnly: false,
};

export default DataManagement;
